import { createApi } from "@reduxjs/toolkit/query/react";
import { Ewallet } from "types/ewallet";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import type {
  WithdrawalDepositAccountOptionRow,
  WithdrawalDepositAccountOptionsResponse,
} from "helpers/withdrawalDepositAccountOptions";
import {
  extractWithdrawalDepositFormsList,
  buildWithdrawalDepositFormsQueryString,
  withdrawalDepositFormInvalidateTags,
  withdrawalDepositNotesInvalidateTags,
} from "helpers/withdrawalDepositApiHelpers";
import {
  WithdrawalDepositFormStatus,
  type WithdrawalDepositApprovalLevelValue,
  WITHDRAWAL_DEPOSIT_FORM_LIST_TAG,
} from "helpers/withdrawalDepositConstants";
import type {
  WithdrawalDepositForm,
  WithdrawalDepositFormNotePayload,
  WithdrawalDepositFormPayload,
  WithdrawalDepositFormTransactionsResponse,
  WithdrawalDepositFormsListResponse,
  WithdrawalDepositFormsQueryArg,
} from "types/withdrawalDeposit";

interface EwalletResponse {
  data: Ewallet[];
}

export type {
  WithdrawalDepositBankDetails,
  WithdrawalDepositForm,
  WithdrawalDepositFormNotePayload,
  WithdrawalDepositFormPayload,
  WithdrawalDepositFormTransactionsResponse,
  WithdrawalDepositFormsListResponse,
  WithdrawalDepositFormsPagination,
  WithdrawalDepositFormsQueryArg,
  WithdrawalDepositLedgerRow,
  WithdrawalDepositRequestSource,
} from "types/withdrawalDeposit";

export type {
  WithdrawalDepositAccountBankRow,
  WithdrawalDepositAccountEwalletRow,
  WithdrawalDepositAccountOptionRow,
  WithdrawalDepositAccountOptionsResponse,
  WithdrawalDepositAccountPickerLine,
  WithdrawalDepositCompanyOption,
} from "helpers/withdrawalDepositAccountOptions";

export {
  applyDefaultsAfterCompanySelect,
  bankAccountMenuLabel,
  buildWithdrawalDepositAccountPickerLines,
  companyPickerOptionsFromAccounts,
  currenciesFromWithdrawalDepositAccounts,
  ewalletCurrencyMenuLabel,
  ewalletMenuLabel,
  getAccountRowById,
  pickSelectableAccountEwallets,
} from "helpers/withdrawalDepositAccountOptions";

export const ewalletApi = createApi({
  reducerPath: "ewalletApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/dashboard`,
  }),
  tagTypes: [
    "EWALLETS",
    "EWALLET_TRANSACTIONS",
    "WITHDRAWAL_DEPOSIT_FORM",
    "WITHDRAWAL_DEPOSIT_NOTES",
  ],
  endpoints: (builder) => ({
    getDashboardEwallets: builder.query<
      EwalletResponse,
      { userId?: string; funder_id?: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.funder_id) {
          queryParams.append("funder_id", params.funder_id);
        }

        const queryString = queryParams.toString();
        return {
          url: `/ewallets${queryString ? `?${queryString}` : ""}`,
          method: "get",
        };
      },
      transformResponse: (response) => response,
      providesTags: ["EWALLETS"],
    }),

    getEwalletTransactions: builder.query<any, { transparams: any }>({
      query: ({ transparams }) => {
        return {
          url: `/transactions?${transparams}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response?.data,
      providesTags: ["EWALLET_TRANSACTIONS"],
    }),
    getWithdrawalDepositForms: builder.query<
      WithdrawalDepositFormsListResponse,
      WithdrawalDepositFormsQueryArg
    >({
      query: (params) => ({
        url: `/withdrawl-deposit/forms${buildWithdrawalDepositFormsQueryString(params)}`,
        method: "GET",
      }),
      transformResponse: (
        response: unknown,
      ): WithdrawalDepositFormsListResponse =>
        extractWithdrawalDepositFormsList(response),
      providesTags: (result) => {
        const tags: Array<
          | "EWALLET_TRANSACTIONS"
          | { type: "WITHDRAWAL_DEPOSIT_FORM"; id: string }
        > = [
          "EWALLET_TRANSACTIONS",
          {
            type: "WITHDRAWAL_DEPOSIT_FORM",
            id: WITHDRAWAL_DEPOSIT_FORM_LIST_TAG,
          },
        ];
        if (Array.isArray(result?.forms)) {
          for (const row of result.forms) {
            if (row?.id) {
              tags.push({ type: "WITHDRAWAL_DEPOSIT_FORM", id: row.id });
            }
          }
        }
        return tags;
      },
    }),
    getWithdrawalDepositForm: builder.query<WithdrawalDepositForm, string>({
      query: (formId) => ({
        url: `/withdrawl-deposit/forms/${formId}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data ?? response,
      providesTags: (_result, _err, formId) => [
        { type: "WITHDRAWAL_DEPOSIT_FORM", id: formId },
      ],
    }),
    getWithdrawalDepositAccountOptions: builder.query<
      WithdrawalDepositAccountOptionsResponse,
      { query?: string; limit?: number } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        const q = (params?.query ?? "").trim();
        if (q) {
          queryParams.append("query", q);
        }
        const limit = params?.limit ?? 50;
        queryParams.append("limit", String(Math.min(Math.max(limit, 1), 50)));
        const qs = queryParams.toString();
        return {
          url: `/withdrawl-deposit/account-options${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (
        response: any,
      ): WithdrawalDepositAccountOptionsResponse => {
        const raw = response?.data ?? response;
        const rawAccounts = raw?.accounts;
        if (!Array.isArray(rawAccounts)) {
          return { accounts: [] };
        }
        if (rawAccounts.length > 0 && typeof rawAccounts[0] === "string") {
          return {
            accounts: (rawAccounts as string[]).map((s) => ({
              id: s,
              name: s,
              bank_accounts: [],
              ewallets: [],
              trustee_ewallets: [],
            })),
          };
        }
        return { accounts: rawAccounts as WithdrawalDepositAccountOptionRow[] };
      },
    }),
    createWithdrawalDepositForm: builder.mutation<
      WithdrawalDepositForm,
      WithdrawalDepositFormPayload
    >({
      query: (data) => ({
        url: "/withdrawl-deposit/forms",
        method: "POST",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (res) => {
        const tags: Array<
          | "EWALLET_TRANSACTIONS"
          | { type: "WITHDRAWAL_DEPOSIT_FORM"; id: string }
        > = [
          "EWALLET_TRANSACTIONS",
          {
            type: "WITHDRAWAL_DEPOSIT_FORM",
            id: WITHDRAWAL_DEPOSIT_FORM_LIST_TAG,
          },
        ];
        if (res?.id) {
          tags.push({ type: "WITHDRAWAL_DEPOSIT_FORM", id: res.id });
        }
        return tags;
      },
    }),
    updateWithdrawalDepositForm: builder.mutation<
      WithdrawalDepositForm,
      { formId: string; data: WithdrawalDepositFormPayload }
    >({
      query: ({ formId, data }) => ({
        url: `/withdrawl-deposit/forms/${formId}`,
        method: "PUT",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (_res, _err, { formId }) =>
        withdrawalDepositFormInvalidateTags(formId),
    }),
    requestWithdrawalDepositApproval: builder.mutation<
      { data?: WithdrawalDepositForm; message?: string },
      {
        formId: string;
        approvalLevel: WithdrawalDepositApprovalLevelValue;
        idempotencyKey?: string;
      }
    >({
      query: ({ formId, approvalLevel, idempotencyKey }) => ({
        url: `/withdrawl-deposit/forms/${formId}/request-approval`,
        method: "POST",
        data: { approval_level: approvalLevel },
        headers: idempotencyKey
          ? { "Idempotency-Key": idempotencyKey }
          : undefined,
      }),
      transformResponse: (response: any) => ({
        data: (response?.data ?? response) as WithdrawalDepositForm,
        message: response?.message as string | undefined,
      }),
      invalidatesTags: (_res, _err, { formId }) =>
        withdrawalDepositFormInvalidateTags(formId),
      async onQueryStarted({ formId }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          if (result?.data) {
            dispatch(
              ewalletApi.util.updateQueryData(
                "getWithdrawalDepositForm",
                formId,
                () => result.data as WithdrawalDepositForm,
              ),
            );
          }
        } catch {
          /* cache unchanged on failure */
        }
      },
    }),
    rejectWithdrawalDepositForm: builder.mutation<
      { data?: WithdrawalDepositForm; message?: string },
      {
        formId: string;
        approvalLevel: WithdrawalDepositApprovalLevelValue;
        reason: string;
        terminalStatus?:
          | typeof WithdrawalDepositFormStatus.REJECTED
          | typeof WithdrawalDepositFormStatus.CANCELLED;
        idempotencyKey?: string;
      }
    >({
      query: ({
        formId,
        approvalLevel,
        reason,
        terminalStatus,
        idempotencyKey,
      }) => ({
        url: `/withdrawl-deposit/forms/${formId}/reject`,
        method: "POST",
        data: {
          approval_level: approvalLevel,
          reason,
          terminal_status:
            terminalStatus ?? WithdrawalDepositFormStatus.REJECTED,
        },
        headers: idempotencyKey
          ? { "Idempotency-Key": idempotencyKey }
          : undefined,
      }),
      transformResponse: (response: any) => ({
        data: (response?.data ?? response) as WithdrawalDepositForm,
        message: response?.message as string | undefined,
      }),
      invalidatesTags: (_res, _err, { formId }) =>
        withdrawalDepositFormInvalidateTags(formId),
    }),
    getWithdrawalDepositFormTransactions: builder.query<
      WithdrawalDepositFormTransactionsResponse,
      string
    >({
      query: (formId) => ({
        url: `/withdrawl-deposit/forms/${formId}/transactions`,
        method: "GET",
      }),
      transformResponse: (
        response: any,
      ): WithdrawalDepositFormTransactionsResponse => {
        const data = response?.data ?? response;
        const transactions = data?.transactions;
        return {
          transactions: Array.isArray(transactions) ? transactions : [],
          sf_withdrawal_deposit_id: data?.sf_withdrawal_deposit_id ?? null,
        };
      },
      providesTags: (_result, _err, formId) => [
        { type: "WITHDRAWAL_DEPOSIT_FORM", id: formId },
      ],
    }),
    getWithdrawalDepositFormNotes: builder.query<{ notes: any[] }, string>({
      query: (formId) => ({
        url: `/withdrawl-deposit/forms/${formId}/notes`,
        method: "GET",
      }),
      transformResponse: (response: any): { notes: any[] } => {
        const data = response?.data ?? response;
        const notes = data?.notes;
        return { notes: Array.isArray(notes) ? notes : [] };
      },
      providesTags: (_result, _err, formId) => [
        { type: "WITHDRAWAL_DEPOSIT_NOTES", id: formId },
      ],
    }),
    createWithdrawalDepositFormNote: builder.mutation<
      any,
      { formId: string; body: WithdrawalDepositFormNotePayload }
    >({
      query: ({ formId, body }) => ({
        url: `/withdrawl-deposit/forms/${formId}/notes`,
        method: "POST",
        data: body,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (_res, _err, { formId }) =>
        withdrawalDepositNotesInvalidateTags(formId),
    }),
    deleteWithdrawalDepositFormNote: builder.mutation<
      { id?: string; deleted?: boolean },
      { formId: string; noteId: string }
    >({
      query: ({ formId, noteId }) => ({
        url: `/withdrawl-deposit/forms/${formId}/notes/${noteId}`,
        method: "DELETE",
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (_res, _err, { formId }) =>
        withdrawalDepositNotesInvalidateTags(formId),
    }),
  }),
});

export const {
  useGetDashboardEwalletsQuery,
  useGetEwalletTransactionsQuery,
  useGetWithdrawalDepositFormsQuery,
  useGetWithdrawalDepositFormQuery,
  useGetWithdrawalDepositAccountOptionsQuery,
  useCreateWithdrawalDepositFormMutation,
  useUpdateWithdrawalDepositFormMutation,
  useRequestWithdrawalDepositApprovalMutation,
  useRejectWithdrawalDepositFormMutation,
  useGetWithdrawalDepositFormTransactionsQuery,
  useGetWithdrawalDepositFormNotesQuery,
  useCreateWithdrawalDepositFormNoteMutation,
  useDeleteWithdrawalDepositFormNoteMutation,
} = ewalletApi;
